import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './JobDisplay.css';
const host_url = "/api";

function JobDisplay() {
    const [settings, setSettings] = useState({ showEinheit: false, einheitId: null });
    const [einheiten, setEinheiten] = useState([]);
    const [zuteilungen, setZuteilungen] = useState([]);
    const [einheitInfo, setEinheitInfo] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [countdown, setCountdown] = useState(null);
    const [nextEinheit, setNextEinheit] = useState(null);

    // Fetch display settings
    const fetchSettings = useCallback(async () => {
        try {
            const response = await axios.get(`${host_url}/display_settings`);
            setSettings(response.data);
        } catch (error) {
            console.error('Error fetching display settings:', error);
        }
    }, []);

    // Fetch all Einheiten
    const fetchEinheiten = useCallback(async () => {
        try {
            const response = await axios.get(`${host_url}/einheiten_mit_zeiten`);
            setEinheiten(response.data);
        } catch (error) {
            console.error('Error fetching einheiten:', error);
        }
    }, []);

    // Fetch zuteilungen for current Einheit
    const fetchZuteilungen = useCallback(async (einheitId) => {
        if (!einheitId) return;
        try {
            const response = await axios.get(`${host_url}/zuteilungen_grouped?einheitId=${einheitId}`);
            setZuteilungen(response.data);
            if (response.data.length > 0) {
                setEinheitInfo({
                    name: response.data[0].EinheitName,
                    start: response.data[0].EinheitStart,
                    ende: response.data[0].EinheitEnde
                });
            }
        } catch (error) {
            console.error('Error fetching zuteilungen:', error);
        }
    }, []);

    // Find next upcoming Einheit for countdown
    const findNextEinheit = useCallback(() => {
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const currentTimeStr = now.toTimeString().split(' ')[0];

        for (const einheit of einheiten) {
            const einheitDate = new Date(einheit.Datum).toISOString().split('T')[0];

            if (einheitDate > today) {
                return einheit;
            } else if (einheitDate === today && einheit.Start > currentTimeStr) {
                return einheit;
            }
        }
        return null;
    }, [einheiten]);

    // Calculate countdown
    const calculateCountdown = useCallback((einheit) => {
        if (!einheit) return null;

        const now = new Date();
        const einheitDate = new Date(einheit.Datum).toISOString().split('T')[0];
        const [hours, minutes] = einheit.Start.split(':');
        const targetTime = new Date(`${einheitDate}T${hours}:${minutes}:00`);

        const diff = targetTime - now;
        if (diff <= 0) return null;

        const totalSeconds = Math.floor(diff / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const h = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;

        return { days: days, hours: h, minutes: m, seconds: s };
    }, []);

    // Calculate custom countdown
    const calculateCustomCountdown = useCallback((targetTimeStr) => {
        if (!targetTimeStr) return null;

        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const [hours, minutes] = targetTimeStr.split(':');
        let targetTime = new Date(`${dateStr}T${hours}:${minutes}:00`);

        // If target time is past, show 0 or handle logic. Assuming today future time.
        let diff = targetTime - now;
        if (diff <= 0) return null;

        const totalSeconds = Math.floor(diff / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const h = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;

        return { days: days, hours: h, minutes: m, seconds: s };
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchSettings();
        fetchEinheiten();
    }, [fetchSettings, fetchEinheiten]);

    // Polling for updates every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchSettings();
            fetchEinheiten();
        }, 10000);
        return () => clearInterval(interval);
    }, [fetchSettings, fetchEinheiten]);

    // Update clock every second
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Calculate effective settings (Auto vs Manual)
    const getEffectiveSettings = () => {
        if (settings.manualOverride) {
            return settings;
        }

        // Auto Mode
        const now = currentTime;

        // Nachtruhe: zwischen 22:15 und 7:30 nur schwarzer Bildschirm
        const minutesOfDay = now.getHours() * 60 + now.getMinutes();
        const nightStart = 22 * 60 + 15; // 22:15
        const nightEnd = 7 * 60 + 30;    // 07:30
        if (minutesOfDay >= nightStart || minutesOfDay < nightEnd) {
            return { showBlack: true, showEinheit: false, einheitId: null };
        }

        const bufferMs = 15 * 60 * 1000; // 15 minutes

        for (const einheit of einheiten) {
            const dateStr = new Date(einheit.Datum).toISOString().split('T')[0];
            const startDateTime = new Date(`${dateStr}T${einheit.Start}`);
            const endDateTime = new Date(`${dateStr}T${einheit.Ende}`);

            const showStart = new Date(startDateTime.getTime() - bufferMs);
            const showEnd = new Date(endDateTime.getTime() - bufferMs);

            if (now >= showStart && now < showEnd) {
                return { showEinheit: true, einheitId: einheit.ID };
            }
        }

        // Default: Countdown
        return { showEinheit: false, einheitId: null };
    };

    const effectiveSettings = getEffectiveSettings();

    // Fetch zuteilungen when effective settings dictate showing a unit
    useEffect(() => {
        if (effectiveSettings.showEinheit && effectiveSettings.einheitId) {
            fetchZuteilungen(effectiveSettings.einheitId);
        }
    }, [effectiveSettings.showEinheit, effectiveSettings.einheitId, fetchZuteilungen]);

    // Update countdown logic
    useEffect(() => {
        if (!effectiveSettings.showEinheit && !effectiveSettings.showCustom) {
            const next = findNextEinheit();
            setNextEinheit(next);
            setCountdown(calculateCountdown(next));
        }
    }, [effectiveSettings.showEinheit, effectiveSettings.showCustom, currentTime, findNextEinheit, calculateCountdown]);

    // Effect for custom countdown
    useEffect(() => {
        if (effectiveSettings.showCustom && effectiveSettings.customTime) {
            setCountdown(calculateCustomCountdown(effectiveSettings.customTime));
        }
    }, [effectiveSettings.showCustom, effectiveSettings.customTime, currentTime, calculateCustomCountdown]);

    // Group zuteilungen by Zelt
    const groupedByZelt = zuteilungen.reduce((acc, z) => {
        if (!acc[z.ZeltName]) {
            acc[z.ZeltName] = [];
        }
        acc[z.ZeltName].push({ kindName: z.KindName, jobName: z.JobName, handwerksjob: z.Handwerksjob });
        return acc;
    }, {});

    const formatTime = (date) => {
        return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    };

    const formatCountdown = (cd) => {
        if (!cd) return '--:--';
        const parts = [];
        if (cd.days > 0) parts.push(`${cd.days} Tage`);
        parts.push(`${cd.hours} Stunden`);
        parts.push(`${cd.minutes} Minuten`);
        return `Noch ${parts.join(', ')}`;
    };

    // Blackscreen Mode (Nachtruhe oder manuell aktiviert)
    if (effectiveSettings.showBlack) {
        return (
            <div className="job-display black-mode">
                <div className="black-mode-time">
                    {formatTime(currentTime)}
                </div>
            </div>
        );
    }

    // Render Custom Mode
    if (effectiveSettings.showCustom) {
        return (
            <div className="job-display countdown-mode" style={{ position: 'relative' }}>
                <div className="countdown-container">
                    <div className="current-time">
                        {formatTime(currentTime)}
                    </div>

                    <div className="next-einheit-label">
                        Aktueller Programmpunkt:
                    </div>
                    <div className="next-einheit-name" style={{ fontSize: '6rem', marginBottom: '1rem' }}>
                        {effectiveSettings.customText || '...'}
                    </div>

                    {effectiveSettings.customTime && countdown && (
                        <>
                            <div className="countdown-timer">
                                {formatCountdown(countdown)}
                            </div>
                            <div className="countdown-label">
                                bis {effectiveSettings.customTime} Uhr
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Countdown Mode
    if (!effectiveSettings.showEinheit) {
        return (
            <div className="job-display countdown-mode" style={{ position: 'relative' }}>
                <div className="countdown-container">
                    <div className="current-time">
                        {formatTime(currentTime)}
                    </div>
                    {nextEinheit ? (
                        <>
                            <div className="next-einheit-label">
                                Nächste Einheit:
                            </div>
                            <div className="next-einheit-name">
                                {nextEinheit.Name}
                            </div>
                            <div className="countdown-timer">
                                {formatCountdown(countdown)}
                            </div>
                            <div className="countdown-label">
                                bis zum Start
                            </div>
                        </>
                    ) : (
                        <div className="no-einheit-message">
                            Keine kommende Einheit geplant
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Einheit Display Mode — horizontal "Sealife"-Laufband:
    // 3 Zelte sichtbar, laufen langsam nach links, neue rücken von rechts nach.
    const zeltEntries = Object.entries(groupedByZelt);
    const SECONDS_PER_CARD = 10; // höher = langsamer (Zeit, bis ein Zelt eine Spaltenbreite weiterwandert)
    const needsScroll = zeltEntries.length > 3; // nur scrollen, wenn nicht alles auf den Schirm passt
    // Inhalt verdoppeln -> translateX(-50%) ergibt einen nahtlosen Endlos-Loop
    const trackItems = needsScroll ? [...zeltEntries, ...zeltEntries] : zeltEntries;
    const marqueeDuration = Math.max(zeltEntries.length * SECONDS_PER_CARD, 24);

    return (
        <div className="job-display einheit-mode-h">
            <header className="einheit-header">
                <h1>{einheitInfo?.name || 'Lade...'}</h1>
                {einheitInfo && (
                    <div className="einheit-time">
                        {einheitInfo.start?.slice(0, 5)} - {einheitInfo.ende?.slice(0, 5)} Uhr
                    </div>
                )}
            </header>

            {zeltEntries.length === 0 ? (
                <div className="no-data-message">
                    Keine Zuteilungen für diese Einheit vorhanden
                </div>
            ) : (
                <div className="marquee-viewport">
                    <div
                        className={`marquee-track ${needsScroll ? 'animate' : 'centered'}`}
                        style={needsScroll ? { animationDuration: `${marqueeDuration}s` } : undefined}
                    >
                        {trackItems.map(([zeltName, kinder], idx) => (
                            <div key={`${zeltName}-${idx}`} className="zelt-card">
                                <h2 className="zelt-name">{zeltName}</h2>
                                <table className="kinder-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Job</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {kinder.map((kind, i) => (
                                            <tr key={i}>
                                                <td>{kind.kindName}</td>
                                                <td>{kind.jobName}{kind.handwerksjob ? <span className="material-symbols-outlined" style={{ fontSize: 'inherit', verticalAlign: 'middle', marginLeft: '0.3em' }}>handyman</span> : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default JobDisplay;
